import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft, CalendarDays, CircleCheck, FileDown, FilePlus2, MapPin, XCircle } from 'lucide-react';
import { api } from '../../api';
import { pode as podeFazer, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { tiposDe } from '../../componentes/formatos';
import { PainelOs } from '../agenda/Agenda';
import { Fotos, type Anexo } from './Fotos';
import { Pecas, type Peca } from './Pecas';
import { ChecklistOs } from '../checklists/ChecklistOs';
import { BlocosChecklist, type BlocoOs } from './BlocosChecklist';
import type { ChecklistOs as Checklist } from '../checklists/comum';
import {
  NOME_ACAO_ATIVO, NOME_EVENTO_OS, NOME_PRIORIDADE, NOME_RESULTADO, NOME_STATUS_OS, distanciaTexto, duracaoTexto, hora,
  type ConfigAgenda, type OsAgenda, type Tecnico,
} from '../agenda/comum';

interface Ficha extends OsAgenda {
  relato: string | null; causa_raiz: string | null; resultado: string | null; motivo_cancelamento: string | null; fechada_em: string | null;
  criado_em: string; criada_por: string | null; instrucoes_acesso: string | null; raio_geofence_m: number;
  ativos: { ativo_id: string; apelido: string; categoria: string; acao: string; diagnostico: string | null }[];
  eventos: { id: string; tipo: string; em: string; recebido_em: string; distancia_local_m: string | null; dentro_geofence: boolean | null; precisao_m: number | null; detalhe: Record<string, string | boolean | null>; usuario: string | null }[];
  adendos: { id: string; motivo: string; alteracao: { texto?: string }; em: string; autor: string }[];
  transicoes: string[];
  anexos: Anexo[];
  checklist?: Checklist;
  pecas?: Peca[];
  chamado_id?: string | null; chamado_numero?: string | null; plantao?: boolean; categoria?: string | null;
  contrato_id?: string | null; contrato_numero?: string | null; contrato_tipo?: string | null; prazo_sla_atendimento?: string | null;
  ativos_local?: { id: string; apelido: string }[];
  plano_id?: string | null; plano?: string | null; visita?: number | null; sistemas?: string[] | null; blocos_checklist?: BlocoOs[] | null;
}

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

export function DetalheOs() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const pode = podeFazer(usuario, 'os.agendar');
  const [os, setOs] = useState<Ficha | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [modal, setModal] = useState<'agenda' | 'cancelar' | 'concluir' | 'adendo' | null>(null);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [cfg, setCfg] = useState<ConfigAgenda>({ inicio: '07:00', fim: '19:00', duracao_padrao_min: 60, fuso: 'America/Sao_Paulo' });

  const carregar = useCallback(() => { api<Ficha>(`/os/${id}`).then(setOs).catch((e) => setErro(e.message)); }, [id]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    if (!pode) return;
    api<Tecnico[]>('/tecnicos').then(setTecnicos).catch(() => {});
    api<ConfigAgenda>('/agenda/config').then(setCfg).catch(() => {});
  }, [pode]);

  if (erro) return <p className="alerta" role="alert">{erro}</p>;
  if (!os) return <p className="nota">Carregando…</p>;

  const fechada = ['concluida', 'cancelada'].includes(os.status);
  const podeAgendar = pode && ['aberta', 'agendada'].includes(os.status);
  const aoSalvar = (nova?: Ficha) => { setModal(null); if (nova) setOs(nova); else carregar(); };

  return (
    <>
      <Link to="/os" className="voltar"><ArrowLeft size={16} aria-hidden /> Ordens de serviço</Link>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">{os.tipo}</span>
          <h1 className="titulo mono">{os.numero}</h1>
          <div className="meta">
            <span className={`status st-${os.status}`}>{NOME_STATUS_OS[os.status]}</span>
            <span className={`pill-prioridade p-${os.prioridade}`}>{NOME_PRIORIDADE[os.prioridade]}</span>
            {os.plantao && <span className="pill-prioridade p-critica">Plantão</span>}
            {os.categoria && <span className="pill-breve">{os.categoria}</span>}
            <span className="com-icone"><MapPin size={14} aria-hidden /> <Link to={`/clientes/${os.cliente_id}`}>{os.cliente}</Link> · {os.local}</span>
          </div>
        </div>
        <div className="acoes-cabecalho">
          <a className="btn-fantasma" href={`/api/os/${os.id}/relatorio.pdf`} target="_blank" rel="noreferrer"><FileDown size={16} aria-hidden /> Relatório PDF</a>
        </div>
        {pode && (
          <div className="acoes-cabecalho">
            {podeAgendar && <button className="btn-fantasma" onClick={() => setModal('agenda')}><CalendarDays size={16} aria-hidden /> {os.status === 'agendada' ? 'Remarcar' : 'Agendar'}</button>}
            {os.transicoes.includes('concluida') && <button className="btn-fantasma" onClick={() => setModal('concluir')}><CircleCheck size={16} aria-hidden /> Concluir</button>}
            {os.transicoes.includes('cancelada') && <button className="btn-fantasma perigo" onClick={() => setModal('cancelar')}><XCircle size={16} aria-hidden /> Cancelar OS</button>}
            {fechada && <button className="btn-fantasma" onClick={() => setModal('adendo')}><FilePlus2 size={16} aria-hidden /> Adendo</button>}
          </div>
        )}
      </div>

      <div className="grade-ficha">
        <section className="cartao ficha">
          <h2>O que foi pedido</h2>
          <p className="texto-longo">{os.descricao}</p>
          <dl>
            <div><dt>Agendada</dt><dd>{os.agendada_para ? `${dataHora(os.agendada_para)} · ${duracaoTexto(os.janela_min)}` : 'A agendar'}</dd></div>
            <div><dt>Técnico</dt><dd>{os.responsavel ?? '—'}</dd></div>
            <div><dt>Aberta</dt><dd>{dataHora(os.criado_em)}{os.criada_por ? ` por ${os.criada_por}` : ''}</dd></div>
            <div><dt>Endereço</dt><dd>{os.endereco || '—'}</dd></div>
            {os.chamado_id && <div><dt>Chamado</dt><dd><Link to={`/chamados/${os.chamado_id}`}>{os.chamado_numero}</Link></dd></div>}
            {os.plano_id && <div><dt>Plano de preventiva</dt><dd>{os.plano}{os.visita ? ` · visita ${os.visita}` : ''}{os.competencia ? ` · ${os.competencia.slice(5, 7)}/${os.competencia.slice(0, 4)}` : ''}{os.data_sugerida ? ` · sugerida ${os.data_sugerida.split('-').reverse().join('/')}` : ''}</dd></div>}
            <div><dt>Contrato</dt><dd>{os.contrato_id ? <Link to={`/contratos/${os.contrato_id}`}>{os.contrato_numero}</Link> : 'Sem contrato (avulsa)'}</dd></div>
            <div><dt>Prazo de chegada (SLA)</dt><dd>{(() => {
              if (!os.prazo_sla_atendimento) return os.contrato_id ? 'Sem prazo para esta prioridade' : '—';
              const chegada = os.eventos.find((e) => e.tipo === 'check_in')?.em;
              const prazo = Date.parse(os.prazo_sla_atendimento);
              const ok = chegada ? Date.parse(chegada) <= prazo : Date.now() <= prazo;
              return <>{dataHora(os.prazo_sla_atendimento)} <span className={`situacao ${ok ? (chegada ? 's-ok' : 's-na') : 's-nok'}`}>{chegada ? (ok ? 'cumprido' : 'estourado') : ok ? 'no prazo' : 'atrasado'}</span></>;
            })()}</dd></div>
          </dl>
          {os.instrucoes_acesso && <p className="instrucoes-acesso"><strong>Acesso:</strong> {os.instrucoes_acesso}</p>}
        </section>

        <section className="cartao ficha">
          <h2>Execução</h2>
          {os.status === 'cancelada' ? (
            <p className="texto-longo"><strong>Cancelada:</strong> {os.motivo_cancelamento}</p>
          ) : os.relato ? (
            <>
              <dl>
                <div><dt>Resultado</dt><dd>{os.resultado ? NOME_RESULTADO[os.resultado] : '—'}</dd></div>
                <div><dt>Fechada</dt><dd>{os.fechada_em ? dataHora(os.fechada_em) : '—'}</dd></div>
              </dl>
              <h3 className="subtitulo">Relato</h3>
              <p className="texto-longo">{os.relato}</p>
              {os.causa_raiz && <><h3 className="subtitulo">Causa</h3><p className="texto-longo">{os.causa_raiz}</p></>}
            </>
          ) : <p className="nota">O relato aparece aqui quando o técnico concluir pelo app.</p>}
          {os.ativos.length > 0 && (
            <>
              <h3 className="subtitulo">Equipamentos atendidos</h3>
              <ul className="lista-ativos-os">
                {os.ativos.map((a) => (
                  <li key={a.ativo_id}><Link to={`/ativos/${a.ativo_id}`}>{a.apelido}</Link> <span className="role-maza">{NOME_ACAO_ATIVO[a.acao]}</span>
                    <small>{a.categoria}{a.diagnostico ? ` · ${a.diagnostico}` : ''}</small></li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>

      {os.blocos_checklist && <BlocosChecklist<Ficha> osId={os.id} blocos={os.blocos_checklist} sistemas={os.sistemas ?? null} podeEditar={pode} aoMudar={setOs} />}

      {os.checklist && (
        <ChecklistOs osId={os.id} checklist={os.checklist} fotosNoServidor={(os.anexos ?? []).map((a) => a.id)}
          podeResponder={pode && !fechada} iniciada={!['aberta', 'agendada', 'em_deslocamento'].includes(os.status)} aoMudar={carregar} />
      )}

      <Pecas osId={os.id} pecas={os.pecas ?? []} ativos={os.ativos_local ?? []} podeEditar={pode && !fechada} aoMudar={carregar} />

      <Fotos anexos={os.anexos ?? []} raio={os.raio_geofence_m} podeApagar={pode && !fechada} aoMudar={carregar} />

      <section className="secao">
        <h2>Linha do tempo</h2>
        {os.eventos.length === 0 ? <p className="nota">Nenhum registro de campo ainda.</p> : (
          <ol className="linha-tempo">
            {os.eventos.map((e) => {
              const dist = distanciaTexto(e.distancia_local_m);
              return (
                <li key={e.id}>
                  <time>{dataHora(e.em)}</time>
                  <strong>{NOME_EVENTO_OS[e.tipo] ?? e.tipo}</strong>
                  {e.tipo === 'mudanca_status' && e.detalhe.para && <span>{NOME_STATUS_OS[String(e.detalhe.de)]} → {NOME_STATUS_OS[String(e.detalhe.para)]}</span>}
                  {e.detalhe.motivo && <span>“{String(e.detalhe.motivo)}”</span>}
                  {e.tipo === 'etiqueta_lida' && <span>{String(e.detalhe.alvo ?? e.detalhe.codigo)}{e.detalhe.origem === 'galeria' ? ' · da galeria, sem localização' : e.detalhe.origem === 'digitado' ? ' · código digitado' : ''}{e.detalhe.distancia_referencia_m != null ? ` · a ${e.detalhe.distancia_referencia_m} m de ${String(e.detalhe.referencia)}` : ''}</span>}
                  {e.dentro_geofence === true && <span className="geo-ok">no local</span>}
                  {e.dentro_geofence === false && <span className="geo-fora" title={`Raio do local: ${os.raio_geofence_m} m`}>fora do local · {dist}</span>}
                  {e.dentro_geofence == null && dist && <small>{dist}</small>}
                  {e.detalhe.chegou_atrasado && <span className="nota">chegou depois do fechamento (sem sinal)</span>}
                  {e.usuario && <small>por {e.usuario}</small>}
                  {Math.abs(Date.parse(e.recebido_em) - Date.parse(e.em)) > 10 * 60_000 && <small>· enviado às {hora(e.recebido_em)}</small>}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {(fechada || os.adendos.length > 0) && (
        <section className="secao">
          <h2>Adendos <span className="contador">{os.adendos.length}</span></h2>
          <p className="nota">OS fechada não muda. Correções ficam registradas aqui, com autor e motivo, sem apagar o original.</p>
          {os.adendos.map((a) => (
            <article key={a.id} className="cartao adendo">
              <p className="texto-longo">{a.alteracao.texto}</p>
              <small>{dataHora(a.em)} · {a.autor} · motivo: {a.motivo}</small>
            </article>
          ))}
        </section>
      )}

      <Modal titulo={os.numero} aberto={modal === 'agenda'} aoFechar={() => setModal(null)}>
        {modal === 'agenda' && <PainelOs os={os} tecnicos={tecnicos} cfg={cfg} podeAgendar aoSalvar={() => aoSalvar()} aoCancelar={() => setModal(null)} />}
      </Modal>
      <Modal titulo="Cancelar OS" aberto={modal === 'cancelar'} aoFechar={() => setModal(null)}>
        {modal === 'cancelar' && <FormStatus os={os} para="cancelada" aoSalvar={aoSalvar} aoCancelar={() => setModal(null)} />}
      </Modal>
      <Modal titulo="Concluir OS" aberto={modal === 'concluir'} aoFechar={() => setModal(null)}>
        {modal === 'concluir' && <FormStatus os={os} para="concluida" aoSalvar={aoSalvar} aoCancelar={() => setModal(null)} />}
      </Modal>
      <Modal titulo="Adendo" aberto={modal === 'adendo'} aoFechar={() => setModal(null)}>
        {modal === 'adendo' && <FormAdendo os={os} aoSalvar={aoSalvar} aoCancelar={() => setModal(null)} />}
      </Modal>
    </>
  );
}

function FormStatus({ os, para, aoSalvar, aoCancelar }: { os: Ficha; para: 'cancelada' | 'concluida'; aoSalvar: (f: Ficha) => void; aoCancelar: () => void }) {
  const [f, setF] = useState({ motivo: '', resultado: 'resolvido', relato: '', causa_raiz: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      const corpo = para === 'cancelada' ? { versao: os.versao, para, motivo: f.motivo }
        : { versao: os.versao, para, resultado: f.resultado, relato: f.relato, ...(f.causa_raiz ? { causa_raiz: f.causa_raiz } : {}) };
      aoSalvar(await api<Ficha>(`/os/${os.id}/status`, { metodo: 'POST', corpo }));
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }
  return (
    <form className="form" onSubmit={enviar}>
      {para === 'cancelada' ? (
        <label>Motivo do cancelamento<textarea required minLength={5} rows={3} value={f.motivo} onChange={set('motivo')} placeholder="Ex.: cliente resolveu sozinho; OS duplicada; atendido em outra OS" /></label>
      ) : (
        <>
          <p className="nota">Use para OS resolvida sem ir a campo (acesso remoto, orientação por telefone). Em campo, o técnico conclui pelo app, com check-in.</p>
          <label>Resultado<select value={f.resultado} onChange={set('resultado')}>{tiposDe(NOME_RESULTADO).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select></label>
          <label>O que foi feito<textarea required minLength={10} rows={4} value={f.relato} onChange={set('relato')} /></label>
          <label>Causa (opcional)<textarea rows={2} value={f.causa_raiz} onChange={set('causa_raiz')} /></label>
        </>
      )}
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Voltar</button>
        <button className="btn" disabled={salvando}>{salvando ? 'Salvando…' : para === 'cancelada' ? 'Cancelar OS' : 'Concluir OS'}</button>
      </div>
    </form>
  );
}

function FormAdendo({ os, aoSalvar, aoCancelar }: { os: Ficha; aoSalvar: (f: Ficha) => void; aoCancelar: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try { aoSalvar(await api<Ficha>(`/os/${os.id}/adendos`, { metodo: 'POST', corpo: { motivo, texto } })); }
    catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }
  return (
    <form className="form" onSubmit={enviar}>
      <label>Correção ou complemento<textarea required minLength={3} rows={4} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ex.: peça usada foi capacitor 25uF, não 20uF." /></label>
      <label>Por que está sendo corrigido<input required minLength={10} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: técnico informou depois por telefone" /></label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Voltar</button>
        <button className="btn" disabled={salvando || texto.trim().length < 3 || motivo.trim().length < 10}>{salvando ? 'Salvando…' : 'Registrar adendo'}</button>
      </div>
    </form>
  );
}
